"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.wrapRandomWordsInChildren = exports.sampleTerms = exports.wrapTermsInChildren = exports.findTermDefinition = exports.buildTermMatcher = void 0;
var react_1 = __importDefault(require("react"));
var ESCAPE_REGEX = /[.*+?^${}()|[\]\\]/g;
var escapeRegExp = function (value) {
    return value.replace(ESCAPE_REGEX, '\\$&');
};
/**
 * Build a single regex with one alternation group per term. The regex
 * uses a `\b`-style boundary that's safe for unicode word characters.
 */
exports.buildTermMatcher = function (terms) {
    var cleaned = terms.map(function (t) { return (t.term || '').trim(); }).filter(function (t) { return t.length > 0; });
    if (cleaned.length === 0) {
        return null;
    }
    // Sort longest-first so multi-word terms win over single-word ones.
    cleaned.sort(function (a, b) { return b.length - a.length; });
    var alternation = cleaned.map(escapeRegExp).join('|');
    return new RegExp("(\\b(?:" + alternation + ")\\b)", 'gi');
};
exports.findTermDefinition = function (match, terms) {
    var lower = match.toLowerCase();
    for (var i = 0; i < terms.length; i += 1) {
        if (terms[i].term.toLowerCase() === lower) {
            return terms[i];
        }
    }
    return undefined;
};
/**
 * Walks an arbitrary React children tree, splitting any text node by
 * the supplied matcher and replacing matches with the result of
 * `renderTerm`. Non-text nodes are passed through untouched, including
 * deep children of nested elements (e.g. `<strong>`, `<a>`, etc.).
 */
exports.wrapTermsInChildren = function (children, matcher, renderTerm) {
    if (!matcher) {
        return children;
    }
    var walk = function (node, keyPrefix) {
        if (node === null || node === undefined || typeof node === 'boolean') {
            return node;
        }
        if (typeof node === 'string') {
            // Reset stateful regex before each split.
            matcher.lastIndex = 0;
            var parts = node.split(matcher);
            if (parts.length <= 1) {
                return node;
            }
            return parts.map(function (part, idx) {
                if (idx % 2 === 1) {
                    return renderTerm(part, keyPrefix + "-t-" + idx);
                }
                return part;
            });
        }
        if (typeof node === 'number') {
            return node;
        }
        if (Array.isArray(node)) {
            return node.map(function (child, idx) { return walk(child, keyPrefix + "-" + idx); });
        }
        if (react_1.default.isValidElement(node)) {
            var element = node;
            var innerChildren = element.props && element.props.children;
            if (innerChildren === undefined) {
                return element;
            }
            var newChildren = walk(innerChildren, keyPrefix + "-c");
            if (newChildren === innerChildren) {
                return element;
            }
            return react_1.default.cloneElement(element, undefined, newChildren);
        }
        return node;
    };
    return walk(children, 'wt');
};
/**
 * Random sample of `count` items from `pool` (no duplicates). Stable
 * across renders if you pass a seed via simple slicing — for the
 * prototype demo we use `Math.random`.
 */
exports.sampleTerms = function (pool, count) {
    if (pool.length <= count) {
        return pool.slice();
    }
    var copy = pool.slice();
    var out = [];
    for (var i = 0; i < count; i += 1) {
        var idx = Math.floor(Math.random() * copy.length);
        out.push(copy.splice(idx, 1)[0]);
    }
    return out;
};
// Words that are too short or structural to be useful as demo
// highlights — skip them so the picker always lands on something
// readable.
var RANDOM_WORD_STOPLIST = [
    'the',
    'a',
    'an',
    'and',
    'or',
    'but',
    'if',
    'of',
    'in',
    'on',
    'at',
    'to',
    'for',
    'with',
    'by',
    'is',
    'are',
    'was',
    'were',
    'be',
    'been',
    'it',
    'its',
    "it's",
    'this',
    'that',
    'these',
    'those',
    'as',
    'from',
    'into',
    'than',
    'then',
    'so',
    'not',
    'no',
    'do',
    'does',
    'did',
    'has',
    'have',
    'had',
    'will',
    'would',
    'can',
    'could',
    'should',
    'i',
    'we',
    'you',
    'they',
    'he',
    'she',
    'them',
    'us',
    'our',
    'your',
];
var RANDOM_WORD_STOPSET = (function () {
    var map = {};
    RANDOM_WORD_STOPLIST.forEach(function (w) {
        map[w] = true;
    });
    return map;
})();
var RANDOM_WORD_TOKEN = /([\p{L}][\p{L}\p{M}'’-]*)/gu;
var isEligibleWord = function (word, minLength) {
    if (word.length < minLength) {
        return false;
    }
    return !RANDOM_WORD_STOPSET[word.toLowerCase()];
};
// 32-bit FNV-1a hash — fast, deterministic, no deps.
var hashWord = function (word, seed) {
    var h = 0x811c9dc5 ^ seed;
    for (var i = 0; i < word.length; i += 1) {
        h ^= word.charCodeAt(i);
        h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) | 0;
    }
    return h >>> 0;
};
var seedToInt = function (seed) {
    if (seed === undefined) {
        return 0;
    }
    if (typeof seed === 'number') {
        return seed | 0;
    }
    var h = 0;
    for (var i = 0; i < seed.length; i += 1) {
        h = (h * 31 + seed.charCodeAt(i)) | 0;
    }
    return h;
};
/**
 * Walk a React children tree and wrap a subset of word tokens with
 * `renderTerm`. The selection is **deterministic**: each word's hash
 * (mixed with `options.seed`) decides whether it gets wrapped, so the
 * same input always produces the same highlights across re-renders.
 * No dictionary required — useful for demoing the inline term
 * indicator while the real term registry is still being defined.
 */
exports.wrapRandomWordsInChildren = function (children, renderTerm, options) {
    var probability = options && typeof options.probability === 'number' ? options.probability : 0.06;
    var minLength = options && typeof options.minLength === 'number' ? options.minLength : 4;
    var seedInt = seedToInt(options ? options.seed : undefined);
    // Threshold in the 32-bit hash space.
    var threshold = Math.max(0, Math.min(1, probability)) * 0xffffffff;
    var shouldWrap = function (word) {
        if (!isEligibleWord(word, minLength)) {
            return false;
        }
        return hashWord(word.toLowerCase(), seedInt) < threshold;
    };
    var walk = function (node, keyPrefix) {
        if (node === null || node === undefined || typeof node === 'boolean') {
            return node;
        }
        if (typeof node === 'string') {
            RANDOM_WORD_TOKEN.lastIndex = 0;
            var parts = node.split(RANDOM_WORD_TOKEN);
            if (parts.length <= 1) {
                return node;
            }
            var didWrap_1 = false;
            var out = parts.map(function (part, idx) {
                // Odd indices are the captured word tokens.
                if (idx % 2 === 1 && shouldWrap(part)) {
                    didWrap_1 = true;
                    return renderTerm(part, keyPrefix + "-w-" + idx);
                }
                return part;
            });
            return didWrap_1 ? out : node;
        }
        if (typeof node === 'number') {
            return node;
        }
        if (Array.isArray(node)) {
            return node.map(function (child, idx) { return walk(child, keyPrefix + "-" + idx); });
        }
        if (react_1.default.isValidElement(node)) {
            var element = node;
            var innerChildren = element.props && element.props.children;
            if (innerChildren === undefined) {
                return element;
            }
            var newChildren = walk(innerChildren, keyPrefix + "-c");
            if (newChildren === innerChildren) {
                return element;
            }
            return react_1.default.cloneElement(element, undefined, newChildren);
        }
        return node;
    };
    return walk(children, 'rw');
};
